import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import {
  Quote,
  Star,
  Sparkles,
} from "lucide-react";

import testimonials from "../data/testimonials";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};


export default function Testimonial() {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section
      id="testimonial"
      className="relative overflow-hidden py-16 bg-gradient-to-b from-white via-[#faf8ff] to-[#f5f0ff]"
    >
      {/* Background Blobs */}

      <motion.div
        animate={{
          x: [0, 80, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-fuchsia-300/20 blur-[120px]"
      />

      <motion.div
        animate={{
          x: [0, -60, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-violet-300/20 blur-[130px]"
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-purple-100 px-3 py-1.5 sm:px-5 sm:py-2">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />

            <span className="text-[11px] sm:text-sm font-semibold text-purple-700 uppercase">
              Testimonials
            </span>
          </div>

          <h2 className="mt-3 sm:mt-6 text-[28px] sm:text-5xl font-black leading-tight">
            <span className="text-gray-900">
              Client
            </span>

            <br />

            <span className="bg-gradient-to-r from-fuchsia-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
              Success Stories
            </span>
          </h2>

          <p className="mx-auto mt-2 sm:mt-3 max-w-xl sm:max-w-2xl text-[13px] sm:text-base text-gray-600 leading-6 sm:leading-8 px-3 sm:px-0">
            Trusted by startups, businesses and brands who love
            working with GrowX.
          </p>
        </motion.div>

        {/* Navigation */}

        {/* <div className="mt-5 flex justify-end gap-3">

          <button
            ref={prevRef}
            className="flex h-12 w-12 items-center justify-center rounded-full border border-purple-200 bg-white shadow transition hover:bg-purple-600 hover:text-white"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            ref={nextRef}
            className="flex h-12 w-12 items-center justify-center rounded-full border border-purple-200 bg-white shadow transition hover:bg-purple-600 hover:text-white"
          >
            <ChevronRight size={20} />
          </button>

        </div> */}

        {/* Swiper */}

        <motion.div
  variants={container}
  initial="hidden"
  whileInView="show"
  viewport={{ once: true }}
  className="mt-6"
>
  <Swiper
    modules={[Pagination, Navigation, Autoplay]}
    spaceBetween={20}
    slidesPerView={3}
    loop
    autoplay={{
      delay: 3500,
      disableOnInteraction: false,
    }}
    pagination={{
      clickable: true,
    }}
    navigation={{
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    }}
    onBeforeInit={(swiper) => {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }}
    breakpoints={{
      0: {
        slidesPerView: 1,
        spaceBetween: 16,
      },
      640: {
        slidesPerView: 1,
        spaceBetween: 18,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 20,
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 24,
      },
    }}
  >
    {testimonials.map((item, index) => (
      <SwiperSlide key={index} className="pb-3">
        <motion.div
          variants={item}
          whileHover={{
            y: -8,
            scale: 1.02,
          }}
          transition={{ duration: 0.3 }}
          className="group h-full p-2 md:p-3"
        >
          {/* Gradient Border */}

          <div className="h-full rounded-[22px] bg-gradient-to-r from-fuchsia-500 via-purple-600 to-indigo-600 p-[2px]">

            {/* Card */}

            <div className="relative flex h-full flex-col overflow-hidden rounded-[20px] bg-white p-5 md:p-6 shadow-lg transition-all duration-500 group-hover:shadow-[0_20px_50px_rgba(124,58,237,.18)]">

              {/* Quote & Stars */}

              <div className="mb-5 flex items-start justify-between">

                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white shadow-md">

                  <Quote size={18} />

                </div>

                <div className="flex gap-1">

                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={13}
                      className="fill-yellow-400 text-yellow-400"
                    />
                  ))}

                </div>

              </div>

              {/* Review */}

              <p className="flex-1 text-sm md:text-[15px] leading-7 text-gray-600">

                "{item.text}"

              </p>

              {/* Divider */}

              <div className="my-5 h-px bg-gradient-to-r from-transparent via-purple-200 to-transparent" />

              {/* User */}

              <div className="flex items-center gap-3">

                <img
                  src={item.img}
                  alt={item.name}
                  className="h-11 w-11 md:h-12 md:w-12 rounded-full border-2 border-purple-200 object-cover"
                />

                <div>

                  <h4 className="text-base md:text-lg font-bold text-gray-900">

                    {item.name}

                  </h4>

                  <p className="text-xs md:text-sm text-purple-600">

                    {item.role}

                  </p>

                </div>

              </div>

            </div>

          </div>

        </motion.div>
      </SwiperSlide>
    ))}
  </Swiper>
</motion.div>



      </div>

      {/* Swiper Pagination Styling */}

      <style>{`
        .swiper{
          padding-bottom:70px;
        }

        .swiper-pagination{
          bottom:10px !important;
        }

        .swiper-pagination-bullet{
          width:12px;
          height:12px;
          background:#d8b4fe;
          opacity:1;
          transition:.3s;
        }

        .swiper-pagination-bullet-active{
          width:34px;
          border-radius:999px;
          background:linear-gradient(90deg,#c026d3,#7c3aed,#4f46e5);
        }

        .swiper-slide{
          height:auto;
        }

        .swiper-slide > div{
          height:100%;
        }

        @media (max-width:768px){

          .swiper{
            padding-bottom:60px;
          }

          .swiper-pagination-bullet{
            width:10px;
            height:10px;
          }

          .swiper-pagination-bullet-active{
            width:28px;
          }
        }
      `}</style>

    </section>
  );
}